import type { Lifecycle } from '@/services/dbConsole'
import { useDealDossierStore } from '@/store/dealDossierStore'
import { count } from './format'

/**
 * Live vs archived wording for the Database console.
 *
 * Rows the Settings -> Demo Data "reset test data" switch archived are kept,
 * never deleted, so a raw count includes them. On 29 Oct the console said 407
 * deals against a pipeline of 161 and the 246 archived rows read as duplicates.
 * Every count on these pages for a soft-deleting table therefore says how many
 * rows are live and how many archived, in these words.
 */

/** The table soft-deletes (`archived_at`), so live and archived are told apart. */
export function archives(lifecycle: Lifecycle | null | undefined): lifecycle is Lifecycle {
  return Boolean(lifecycle?.archiveColumn)
}

/** "246 archived by test-data reset", "250 archived (246 by test-data reset)",
 *  or "4 archived" — the reset is named only for rows it provably archived. */
export function archivedPhrase(lifecycle: Lifecycle): string {
  const archived = lifecycle.archivedRows
  const byReset = lifecycle.archivedByReset ?? 0
  if (archived > 0 && byReset === archived) return `${count(archived)} archived by test-data reset`
  if (byReset > 0) return `${count(archived)} archived (${count(byReset)} by test-data reset)`
  return `${count(archived)} archived`
}

/** "161 live · 246 archived by test-data reset". */
export function lifecycleSummary(lifecycle: Lifecycle): string {
  return `${count(lifecycle.liveRows)} live · ${archivedPhrase(lifecycle)}`
}

/** Rows outside the live environment (the demo account's), which the console
 *  lists and labels rather than hiding. Null when there are none. */
export function environmentNote(lifecycle: Lifecycle | null | undefined): string | null {
  const others = (lifecycle?.environments ?? []).filter(
    (entry) => entry.environment !== 'live' && entry.liveRows + entry.archivedRows > 0,
  )
  if (others.length === 0) return null
  const total = others.reduce((n, entry) => n + entry.liveRows + entry.archivedRows, 0)
  const parts = others.map(
    (entry) => `${count(entry.liveRows + entry.archivedRows)} ${entry.environment}`,
  )
  return `Includes ${parts.join(' and ')}-environment row${total === 1 ? '' : 's'}, shown and labeled in data_partition`
}

/** A stored row is archived: its table soft-deletes and the stamp is set. */
export function rowArchived(row: Record<string, unknown>, lifecycle: Lifecycle | null | undefined): boolean {
  if (!lifecycle?.archiveColumn) return false
  const stamp = row[lifecycle.archiveColumn]
  return stamp !== null && stamp !== undefined && stamp !== ''
}

/** Why a `loans` row cannot open as a deal file for this account, or null when it can.
 *  The deal file reads through the working screens' filter (live rows in the
 *  asker's own environment), so anything else would open to "not found". */
export function dealFileBlocker(
  row: Record<string, unknown>,
  archived: boolean,
  viewerEnvironment: string | null | undefined,
): string | null {
  if (archived) {
    return 'This deal is archived. Archived deals are kept but no longer open as a deal file; every stored column is shown here.'
  }
  const environment = row.data_partition
  if (viewerEnvironment && typeof environment === 'string' && environment !== viewerEnvironment) {
    return `This deal belongs to the ${environment} environment. Its deal file opens only for an account in that environment; every stored column is shown here.`
  }
  return null
}

/** Open a deal's file — the same dossier the Pipeline and the Deal Locator open. */
export function openDealFile(loanId: string): void {
  useDealDossierStore.getState().openDossier(loanId)
}
